import { useMemo, useState } from 'react'
import { BackButton } from 'components/common/BackButton'
import { Link, useLocation, useParams } from 'react-router-dom'
import * as api from 'api/entityApi'
import { ErrorState, PageLoader } from 'components/common/Loader'
import { Tabs, type TabDef } from 'components/common/Tabs'
import { useToast } from 'components/common/Toast'
import { DealPipeline } from 'components/view/DealPipeline'
import { OtherDetails, type DetailField } from 'components/view/OtherDetails'
import { ProfilePanel } from 'components/view/ProfilePanel'
import { ScoreCard } from 'components/view/ScoreCard'
import { EntityActionWidget } from 'components/edit/EntityActionWidget'
import { ErrorBoundary } from 'components/common/ErrorBoundary'
import {
  ActivitiesPanel,
  AppointmentsPanel,
  CallsPanel,
  ChatPanel,
  DocumentsPanel,
  FollowUpPanel,
  NotesPanel,
  TasksPanel,
  WebformPanel,
} from 'components/view/tabs/EntityTabs'
import { WaChatLogPanel } from 'components/view/tabs/WaChatLogPanel'
import { useAsyncData } from 'hooks/useAsyncData'
import { useFieldMask, usePermissions } from 'hooks/usePermissions'
import { resolveEntityParams } from 'utils/entityParams'
import SecondaryEntityView from './SecondaryEntityView'
import type { Enquiry, EntityType, Lead } from 'types/entity'

/** Types rendered by the profile + tabs layout; the rest use SecondaryEntityView. */
const PROFILE_LAYOUT: EntityType[] = ['lead', 'enquiry']
const SECONDARY_LAYOUT: EntityType[] = [
  'task',
  'appointment',
  'followup',
  'physicalappointment',
  'calllogdetail',
]

/**
 * Replacement for EntityViewDetails.aspx + jsEntityObj.
 *
 * `CreateEntity` switched on entityType to build a different DOM tree; here the
 * same switch picks which detail fields and tabs to render. Supports encrypted query strings (?q=...).
 */
export default function EntityView() {
  const routeParams = useParams<{ entityType?: EntityType; entityId?: string }>()
  const location = useLocation()
  const toast = useToast()
  const [showPipeline, setShowPipeline] = useState(false)

  // Route path, ?q=… or the legacy ?entityType=lead&entityID=<jsFlowCommon>&EditLead=…
  const { entityType, entityId, accessDenied, skipPermissionLookup } = useMemo(
    () => resolveEntityParams(location.search, routeParams),
    [location.search, routeParams],
  )

  const type = entityType as EntityType
  const isLead = type === 'lead'
  const supported = PROFILE_LAYOUT.includes(type)

  // Role permissions per module (lead PAG10031 / enquiry PAG10002).
  const permissions = usePermissions(
    !skipPermissionLookup,
    api.MODULE_CODES[isLead ? 'lead' : 'enquiry'],
  )
  const { mask } = useFieldMask()

  const entity = useAsyncData<Lead | Enquiry>(
    (signal) => (isLead ? api.fetchLead(entityId, signal) : api.fetchEnquiry(entityId, signal)),
    [type, entityId],
    { enabled: supported && !!entityId },
  )

  const customFields = useAsyncData(
    (signal) => api.fetchViewCustomFields(isLead ? 'lead' : 'enquiry', entityId, signal),
    [type, entityId],
    { enabled: supported && !!entityId },
  )

  const pipeline = useAsyncData((signal) => api.fetchPipelineProgress(entityId, signal), [entityId], {
    enabled: isLead && !!entityId,
  })

  const score = useAsyncData((signal) => api.fetchLeadScore(entityId, signal), [entityId], {
    enabled: isLead && !!entityId,
  })

  const detailFields = useMemo<DetailField[]>(() => {
    const data = entity.data
    if (!data) return []

    if ('leadId' in data) {
      return [
        { label: 'Lead No', value: data.leadNo, badge: true },
        { label: 'Created On', value: data.createdOn },
        { label: 'Source', value: data.source },
        { label: 'Medium', value: data.medium },
        { label: 'Campaign', value: data.campaign },
        { label: 'Created By', value: data.createdBy },
        { label: 'Owner', value: data.owner },
        { label: 'Stage', value: data.stage },
        { label: 'Reminder Date', value: data.reminderDate },
        { label: 'Products', value: data.products },
        { label: 'Amount Paid', value: data.amountPaid },
        { label: 'Stage Remark', value: data.stageRemark, wide: true },
        { label: 'Resi. Address', value: data.residentialAddress, wide: true },
        { label: 'Office Address', value: data.officialAddress, wide: true },
        { label: 'Initial Remarks', value: data.initialRemarks, wide: true },
      ]
    }

    return [
      { label: 'Enquiry ID', value: data.enquiryId, badge: true },
      { label: 'Created On', value: data.createdOn },
      { label: 'Source', value: data.source },
      { label: 'Medium', value: data.medium },
      { label: 'Campaign', value: data.campaign },
      { label: 'Created By', value: data.createdBy },
      { label: 'Owner', value: data.owner },
      { label: 'Stage', value: data.stage },
      { label: 'Reminder Date', value: data.reminderDate },
      { label: 'Stage Remark', value: data.stageRemark, wide: true },
      { label: 'Resi. Address', value: data.residentialAddress, wide: true },
      { label: 'Office Address', value: data.officialAddress, wide: true },
      { label: 'Remarks', value: data.remarks, wide: true },
    ]
  }, [entity.data])

  const tabs = useMemo<TabDef[]>(() => {
    const props = { entityId, entityType: type }
    return [
      { key: 'activities', label: 'Activities', render: () => <ActivitiesPanel {...props} /> },
      { key: 'calls', label: 'Calls', render: () => <CallsPanel {...props} /> },
      { key: 'followup', label: 'Followup', render: () => <FollowUpPanel {...props} /> },
      { key: 'notes', label: 'Notes', render: () => <NotesPanel {...props} /> },
      { key: 'documents', label: 'Documents', render: () => <DocumentsPanel {...props} /> },
      { key: 'task', label: 'Task', render: () => <TasksPanel {...props} /> },
      { key: 'appointment', label: 'Appointment', render: () => <AppointmentsPanel {...props} /> },
      { key: 'chat', label: 'Chat', render: () => <ChatPanel {...props} /> },
      {
        key: 'webform',
        label: 'Webform',
        render: () => <WebformPanel {...props} />,
        hidden: !isLead || !permissions.AddWebform,
      },
      {
        key: 'pipeline',
        label: 'Pipeline',
        hidden: !isLead,
        render: () =>
          pipeline.loading ? (
            <div className="inline-loader">Loading pipeline…</div>
          ) : (
            <DealPipeline stages={pipeline.data ?? []} />
          ),
      },
      {
        key: 'wachat',
        label: 'WaChat Log',
        hidden: !permissions.SendWhatsapp,
        render: () => <WaChatLogPanel entityId={entityId} entityName={isLead ? 'lead' : 'enquiry'} />,
      },
    ]
  }, [entityId, type, isLead, pipeline.loading, pipeline.data, permissions])

  // EntityViewDetails.js sends the user to /AccessDenied.aspx when entityID does
  // not decrypt to a positive id, rather than rendering an empty profile.
  if (accessDenied) {
    return (
      <div className="app-shell">
        <ErrorState message="Access denied — this link's entityID could not be read." />
      </div>
    )
  }

  // Role gate: View === false on the module means no access at all.
  if (permissions.View === false) {
    return (
      <div className="app-shell">
        <ErrorState message="Access denied — your role does not include View permission for this module." />
      </div>
    )
  }

  // task / appointment / followup / physicalappointment / calllogdetail
  if (SECONDARY_LAYOUT.includes(type)) {
    return <SecondaryEntityView entityType={type} entityId={entityId} />
  }

  if (!supported) {
    return (
      <div className="app-shell">
        <ErrorState
          message={`Entity type "${entityType}" is not recognised. Supported: ${[...PROFILE_LAYOUT, ...SECONDARY_LAYOUT].join(', ')}.`}
        />
      </div>
    )
  }

  if (entity.loading) return <PageLoader />

  if (entity.error || !entity.data) {
    return (
      <div className="app-shell">
        <ErrorState
          message={
            entity.error ??
            `No data returned for ${type} ${entityId}. Check that the API is running and that this id exists for user ${api.getSessionContext().userId}.`
          }
          onRetry={entity.reload}
        />
      </div>
    )
  }

  const data = entity.data
  const isLeadData = 'leadId' in data
  const pipelineCount = isLead ? new Set((pipeline.data ?? []).map((s) => s.PinelineName)).size : undefined

  return (
    <div className="app-shell">
      <header className="page-header">
        <div>
          <h1>{isLead ? 'Entity View Details' : 'Enquiry View Details'}</h1>
          <div className="crumbs">
            {type} · {isLeadData ? `Lead No ${data.leadNo}` : `Enquiry ${data.enquiryId}`}
          </div>
        </div>
        <div className="page-actions">
          <BackButton />
          {permissions.Edit && (
            <Link className="btn pri-btn" to={`/edit/${type}/${entityId}`}>
              Edit
            </Link>
          )}
        </div>
      </header>

      <div className="entity-view-layout">
        <div className="entity-main">
          <div className="row">
            <div className="col-4">
              <ProfilePanel
                entityType={type}
                entityId={entityId}
                imageUrl={data.profileImageUrl}
                score={score.data?.score ?? null}
                personName={data.personName}
                companyName={data.companyName}
                mobile={mask('MobileNo', data.mobileNo)}
                email={mask('EmailID', data.emailId)}
                city={data.city}
                state={data.state}
                country={data.country}
                pinCode={data.pinCode}
                lastUpdate={isLeadData ? data.lastUpdate : data.createdOn}
                pipelineCount={pipelineCount}
                tags={data.tags}
                onPipelineClick={() => setShowPipeline((v) => !v)}
                onMobileClick={() => toast.push('info', `Dialling ${data.mobileNo}…`)}
              />
              {score.data && (
                <div style={{ marginTop: 12 }}>
                  <ScoreCard score={score.data} />
                </div>
              )}
            </div>

            <div className="col-8">
              <OtherDetails
                fields={detailFields}
                customFields={customFields.data ?? []}
                customFieldsLoading={customFields.loading}
              />
            </div>
          </div>

          {showPipeline && isLead && (
            <>
              <hr />
              {pipeline.loading ? (
                <div className="inline-loader">Loading pipeline…</div>
              ) : (
                <DealPipeline stages={pipeline.data ?? []} />
              )}
            </>
          )}

          <h4 style={{ marginTop: 16 }}>Recent Activity</h4>
          {/* Drag a tab to change the sequence; the order is remembered per
              entity type, the same way the Sales module's LeadDetail does. */}
          <Tabs tabs={tabs} orderStorageKey={`entityTabsOrder:${isLead ? 'lead' : 'enquiry'}`} />
        </div>

        <aside className="entity-view-sidebar">
          <ErrorBoundary label="The action panel">
            <EntityActionWidget
              entityId={entityId}
              entityType={type === 'enquiry' ? 'enquiry' : 'lead'}
              permissions={permissions}
              contact={{
                mobileNo: data.mobileNo,
                mobileNo1: data.mobileNo1,
                mobileNo2: data.mobileNo2,
                email: data.emailId,
                email1: data.emailId1,
                email2: data.emailId2,
              }}
              onActionSuccess={(msg) => toast.push('success', msg)}
            />
          </ErrorBoundary>
        </aside>
      </div>
    </div>
  )
}

